import React, { useCallback, useMemo } from "react";
import { BlockStack, Text } from "@shopify/polaris";
import {
  applyBarDesignTemplate,
  BAR_DESIGN_TEMPLATES,
  resolveActiveTemplateId,
} from "../../utils/barDesignTemplates";
import { applyTokens, ProgressBarSvg } from "./BarWidget";
import { cardContainerStyle, messageStyle } from "../../utils/barCardSurface";
import { hasAnimatedMessage } from "../../utils/messageAnimation";
import AnimatedMessage from "./AnimatedMessage";

const PREVIEW_MSG = "Spend {order-value-progress} more and receive free shipping!";
const PREVIEW_GOAL = "$60.00";
const PREVIEW_PROGRESS = "$18.00";

function groupTemplates(list) {
  const groups = {};
  list.forEach((t) => {
    if (!groups[t.group]) groups[t.group] = [];
    groups[t.group].push(t);
  });
  return groups;
}

function TemplateMiniPreview({ design }) {
  const msg = applyTokens(PREVIEW_MSG, PREVIEW_GOAL, PREVIEW_PROGRESS);
  return (
    <div className="fsb-template-picker__preview-inner">
      <BarWidgetMini message={msg} design={design} pct={28} />
    </div>
  );
}

/** Compact bar for template list rows. */
function BarWidgetMini({ message, design, pct }) {
  const card = design?.card || {};
  const sp = card.spacing || {};
  const pb = design?.progress_bar || {};
  const msgTypo = design?.typography?.message || {};
  const useAnimation = hasAnimatedMessage(design);
  const miniCardStyle = {
    ...cardContainerStyle(card, "product_page", design?.layout),
    border: `${card.border_size ?? 0}px solid ${card.border_color || "transparent"}`,
    borderRadius: card.corner_radius ?? 6,
    padding: `${Math.min(sp.inside_top ?? 12, 12)}px 12px ${Math.min(sp.inside_bottom ?? 12, 12)}px`,
  };

  const miniMsgStyle = {
    ...messageStyle(design?.typography),
    fontSize: Math.min(msgTypo.size ?? 14, 14),
    marginBottom: 6,
  };

  return (
    <div className="fsb-template-mini-bar" style={miniCardStyle}>
      {useAnimation ? (
        <AnimatedMessage
          message={message}
          design={design}
          classPrefix="fsb-template-mini-bar__anim"
          style={miniMsgStyle}
        />
      ) : (
        <p className="fsb-template-mini-bar__msg" style={miniMsgStyle}>
          {message}
        </p>
      )}
      {pb.visible !== false ? (
        <div style={{ position: "relative", zIndex: 1 }}>
          <ProgressBarSvg pct={pct} pb={pb} />
        </div>
      ) : null}
    </div>
  );
}

function TemplateRow({ template, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`fsb-template-picker__row${selected ? " fsb-template-picker__row--selected" : ""}`}
      onClick={() => onSelect(template.id)}
    >
      <TemplateMiniPreview design={template.design} />
      <Text as="span" variant="bodySm" tone="subdued">
        {template.name}
      </Text>
    </button>
  );
}

export default function TemplatesSection({ design, onApplyTemplate }) {
  const activeId = resolveActiveTemplateId(design);
  const grouped = useMemo(() => groupTemplates(BAR_DESIGN_TEMPLATES), []);

  const onSelectTemplate = useCallback(
    (templateId) => {
      onApplyTemplate(applyBarDesignTemplate(templateId, design));
    },
    [design, onApplyTemplate],
  );

  return (
    <BlockStack gap="500">
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm">
          Choose a template
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          Pick a visual style for your bar. Enable message animation in the Design tab.
        </Text>
      </BlockStack>

      {Object.entries(grouped).map(([group, items]) => (
        <BlockStack key={group} gap="200">
          <Text as="h4" variant="headingXs">
            {group}
          </Text>
          <div className="fsb-template-picker">
            {items.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                selected={activeId === t.id}
                onSelect={onSelectTemplate}
              />
            ))}
          </div>
        </BlockStack>
      ))}
    </BlockStack>
  );
}
